import React from 'react';

import Store from 'store';

import gfm from 'remark-gfm';

import Data from 'data';
import { D2SkillTooltip } from 'tooltips';
import { selectStats } from 'logic/stats';
import { itemTipNode, shrineTipNode, skillTipNode, ItemNode } from 'embed/gametips';
import { SkillIcon } from 'components/calculator/Skill';
  
function ProfileItemLink({ id, children }) {
  const item = Store.useItem(id);
  if (!item) return null;
  return <ItemNode text={children} item={item}/>
}

function SkillLink({ id, level, children }) {
  const state = Store.useSelector(selectStats);
  if (!Data.skills[id]) return null;
  if (level == null) level = state.player?.skills?.[id] || 0;
  return skillTipNode(children, level, id);
}

const Link = ({ children, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>;

function LinkRenderer({ href, children }) {
  const parts = href.split('.');
  switch (parts[0]) {
  case 'item':
    if (parts[1] === 'profile') {
      return <ProfileItemLink id={parts[2]} children={children}/>;
    } else if (parts[1]) {
      const node = itemTipNode(children, parts[1]);
      if (!node) return;
      return <span className="d2p-item">{node}</span>;
    }
    break;
  case 'skill': return (
    <span className="d2p-skill">
      <SkillLink id={parts[1]} level={parts[2]} children={children}/>
    </span>
  );
  case 'shrine': return (
    <span className="d2p-shrine">
      {shrineTipNode(children, parts[1])}
    </span>
  );
  case 'skillicon': return (
    <span className="d2p-skill-icon">
      <D2SkillTooltip id={parts[1]} full canLevel="special">
        <SkillIcon id={parts[1]}/>
      </D2SkillTooltip>
    </span>
  );
  default: return <Link className="d2md-link" href={href}>{children}</Link>;
  }
}

function youtubeId(href) {
  try {
    const url = new URL(href);
    if (url.hostname.match(/^(?:.*\.)?youtube\.com$/i) && url.pathname === "/watch") {
      return url.searchParams.get("v");
    } else if (url.hostname.match(/^youtu\.be$/i)) {
      return url.pathname.substring(1);
    }
  } catch (e) {
    return null;
  }
}

function YoutubePlugin() {
  return function(tree) {
    for (const child of tree.children) {
      if (child.type === "paragraph" && child.children.length === 1 && child.children[0].type === "link") {
        const link = child.children[0];
        const id = youtubeId(link.url);
        if (id && id.match(/^[\w_-]+$/)) {
          child.type = "embed";
          child.children.length = 0;
          child.id = id;
        }
      }
    }  
    return tree;
  }
}

function EmbedRenderer({ id }) {
  return (
    <div className="d2md-embed">
      <iframe width="100%" height="100%" frameBorder="0" src={`https://www.youtube.com/embed/${id}`} title="YouTube"/>
    </div>
  );
}

export const plugins = [gfm, YoutubePlugin];
export const renderers = {link: LinkRenderer, embed: EmbedRenderer};
